import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AtlasRequestCommercialAccount } from 'src/app/models/atlas-request-commercial-account.model';
import { AtlasResponse } from 'src/app/models/atlas-response.model';
import { CargoPaymentInfo } from 'src/app/models/cargo-payment-info.model';
import { Device } from 'src/app/models/device.model';
import { PaymentAuthorizationResponse } from 'src/app/models/icargo/payment-authorization-response.model';
import { PaymentType } from 'src/app/models/enums/payment-type.enum';
import { TransactionResponse, TransactionResult } from 'src/app/models/signal-r-result-ipad.model';
import { DeviceService } from 'src/app/services/device/device.service';
import { ModalService } from 'src/app/services/modal-service/modal.service';
import { OperationMode } from 'src/app/models/enums/operation-mode.enum';
import { PaymentService } from 'src/app/services/paymentService/payment.service';
import { SharedService } from 'src/app/services/shared-service/shared.service';
import { SignalRService } from 'src/app/services/signal-r-service/signal-r.service';
import { AcknowledgeResponse } from 'src/app/models/signal-r.model';
import { environment } from 'src/environments/environment';
import { CustomMessage, mapMessage } from 'src/app/models/enums/custom-message.enum';
import { CustomMessageType } from 'src/app/models/enums/custom-message-type.enum';
import { AtlasErrorCodes, DuplicateTransactionError, InactivityErrorInIPad, MobyError } from 'src/app/models/enums/atlas-error-codes.enum';
import { MonitoringService } from 'src/app/services/monitoring-service/monitoring.service';
import { PaymentTransactionComponent } from '../payment-transaction.component';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FooterComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() cargoPaymentInfo: CargoPaymentInfo | undefined;
  @Input() inputParametersValidationResult?: [boolean, Set<string>];
  @Input() paymentTransactionComponent!: PaymentTransactionComponent;
  @Output() modalTypeEvent = new EventEmitter<string>();
  currentLoadingState = false;
  currentNextClick = false;
  currentContinueClick = false;
  isDisabledExit = false;
  isDisabledNext = false;
  isHiddenCreditCard = false;
  modalType = '';
  transactionResponse: TransactionResponse | undefined;
  buttonName = '';
  isInputParametersValid = true;
  currentClickContinueEvent = false;
  currentClickManualContinueEvent = false;
  currentValidTransaction = true;

  currentSubmitBluefin = false;
  currentManualCardEntryButtonDisabled = false;
  deviceType = 'PP';
  currentAuthCode: string | null = null;

  currentContinueDeviceClick = false;
  currentSelectedIpad: Device | null = null;
  isJasmineTest = false;
  transactionId = '';
  count = 0;
  paymentType = '';
  requestTransactionId = '';
  NoAuth = 'NO_AUTH';
  atlasTransactionResponse!: AtlasResponse;
  private acknowledgementWaitStartedAt: number | undefined;
  private acknowledgementWaitTransactionId: string | undefined;
  private acknowledgementWaitState: string | undefined;
  private acknowledgementEventId: string | undefined;
  private acknowledgementEventClassification: 'Matching' | 'Stale' | 'Unknown' | undefined;
  private acknowledgementWaitTimer: ReturnType<typeof setInterval> | undefined;

  constructor(
    public sharedService: SharedService,
    public modalService: ModalService,
    public signalRService: SignalRService,
    private deviceService: DeviceService,
    private paymentService: PaymentService,
    private monitoringService: MonitoringService
  ) {}
  ngOnInit(): void {
    this.sharedService.currentModalType.pipe(takeUntil(this.destroy$)).subscribe((modalType) => (this.modalType = modalType));

    this.sharedService.currentClickContinueEvent.pipe(takeUntil(this.destroy$)).subscribe((clickContinueEvent) => {
      this.currentClickContinueEvent = clickContinueEvent;
      if (this.currentClickContinueEvent && this.modalType == 'confirmation') this.onContinue();
      else if (this.currentClickContinueEvent && this.modalType == 'connectIpad') this.onSearchIpad();
    });

    this.sharedService.currentClickManualContinueEvent.pipe(takeUntil(this.destroy$)).subscribe((clickManualContinueEvent) => {
      this.currentClickManualContinueEvent = clickManualContinueEvent;
      if (this.currentClickManualContinueEvent && this.modalType == 'manualConfirmation') {
        this.onManualContinue();
      }
    });

    this.sharedService.currentNextClick.pipe(takeUntil(this.destroy$)).subscribe((isNextClick) => (this.currentNextClick = isNextClick));

    this.sharedService.currentContinueClick
      .pipe(takeUntil(this.destroy$))
      .subscribe((isContinueClick) => (this.currentContinueClick = isContinueClick));

    this.sharedService.currentNextDisabled
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDisabledNext) => (this.isDisabledNext = isDisabledNext));

    this.sharedService.currentNextButtonName.pipe(takeUntil(this.destroy$)).subscribe((buttonName) => (this.buttonName = buttonName));

    this.sharedService.currentPaymentType.pipe(takeUntil(this.destroy$)).subscribe((paymentType) => (this.paymentType = paymentType));

    this.sharedService.currentSubmitBluefinClicked
      .pipe(takeUntil(this.destroy$))
      .subscribe((submitBluefin) => (this.currentSubmitBluefin = submitBluefin));

    this.sharedService.currentManualCardEntryButtonDisabled
      .pipe(takeUntil(this.destroy$))
      .subscribe((manualCardEntryButtonDisabled) => (this.currentManualCardEntryButtonDisabled = manualCardEntryButtonDisabled));

    this.sharedService.currentContinueDeviceClick
      .pipe(takeUntil(this.destroy$))
      .subscribe((continueDeviceClick) => (this.currentContinueDeviceClick = continueDeviceClick));

    this.sharedService.currentSelectedIpad
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedIpad) => (this.currentSelectedIpad = selectedIpad));

    this.sharedService.currentLoadingState
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentLoadingState) => (this.currentLoadingState = currentLoadingState));

    this.sharedService.currentExitICargoDisabled
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDisabledExit) => (this.isDisabledExit = isDisabledExit));

    this.sharedService.currentAuthCode.pipe(takeUntil(this.destroy$)).subscribe((authCode) => (this.currentAuthCode = authCode));

    this.sharedService.currentIsHiddenCreditCard
      .pipe(takeUntil(this.destroy$))
      .subscribe((isHidden) => (this.isHiddenCreditCard = isHidden));

    this.sharedService.currentValidTransaction
      .pipe(takeUntil(this.destroy$))
      .subscribe((validTransaction) => (this.currentValidTransaction = validTransaction));

    this.sharedService.currentTransactionId
      .pipe(takeUntil(this.destroy$))
      .subscribe((requestTransactionId) => (this.requestTransactionId = requestTransactionId));

    this.initializeInputParameterValidation();
    void this.getTransactionResultFromIPad();
  }

  ngOnDestroy(): void {
    if (this.acknowledgementWaitTimer !== undefined) {
      clearInterval(this.acknowledgementWaitTimer);
      this.acknowledgementWaitTimer = undefined;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchIpad() {
    this.modalService.close();
    this.onEmitModalType('spinner');
    this.deviceService.getAllDevices().subscribe((deviceList) => {
      if (deviceList.length > 0) {
        this.onEmitModalType('deviceList');
        this.sharedService.updateDeviceList(deviceList);
        this.sharedService.updateClickContinueEvent(false);
      } else {
        this.onEmitModalType('noDevices');
      }
    });
  }

  public onClickNext(): void {
    if (!this.isInputParametersValid || !this.currentValidTransaction) {
      //if Input parameters are not valid, then do nothing, Error message is already on screen
      return;
    }

    if (this.paymentTransactionComponent) {
      this.paymentTransactionComponent.setPendoUrl('next');
    } else {
      console.error('PaymentTransactionComponent is not defined.');
    }

    if (
      this.paymentType == PaymentType.MP &&
      this.cargoPaymentInfo?.operationMode == OperationMode.CC &&
      !this.currentLoadingState &&
      this.buttonName == 'Next' &&
      !this.isNoAuth()
    ) {
      this.onEmitModalType('creditCard');
    } else if (this.paymentType == PaymentType.MP && !this.currentLoadingState && this.buttonName == 'Next' && !this.isNoAuth()) {
      this.onEmitModalType('manualConfirmation');
    } else {
      if (this.buttonName == 'Done' && !this.isButtonDisabled()) {
        this.onDoneClick();
      } else if (
        this.buttonName == 'Next' &&
        !this.currentContinueDeviceClick &&
        this.currentSelectedIpad === null &&
        !this.isNoAuth() &&
        !this.isButtonDisabled() &&
        !this.currentLoadingState
      ) {
        this.sharedService.updateConnectedIpad(true);
        this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnConnect));
      } else if (this.isDisabledNext) {
        this.sharedService.updateNextClick(true);
      } else {
        this.validateIsSignatureRequired();
      }
    }
  }

  validateIsSignatureRequired() {
    if (
      this.isSignatureRequiredForCCAndCL() ||
      (this.cargoPaymentInfo?.signatureRequired?.toUpperCase() === 'N' && this.cargoPaymentInfo?.operationMode === OperationMode.CH)
    ) {
      if (this.cargoPaymentInfo?.paymentAdviceAmount != undefined && this.cargoPaymentInfo?.paymentAdviceAmount > 0) {
        this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoPaymentProgress));
        this.sendRequestToIpad(false);
      } else {
        this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSignaturePayment));
        this.sharedService.updateNextButtonName('Done');
        if (this.isNoAuth()) {
          this.sharedService.updateIsNoSignatureNoPayment(true);
        }
      }
    } else {
      if (this.isNoAuth()) {
        this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSignaturePayment));
        this.sharedService.updateNextButtonName('Done');
        this.sharedService.updateIsNoSignatureNoPayment(true);
      } else if (this.cargoPaymentInfo?.signatureRequirement != undefined && !this.currentLoadingState) {
        this.onEmitModalType('confirmation');
      } else {
        this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoSelectSignatures));
      }
    }
  }

  isSignatureRequiredForCCAndCL(): boolean {
    return (
      (this.cargoPaymentInfo?.signatureRequired == undefined || this.cargoPaymentInfo?.signatureRequired.toLocaleUpperCase() === 'N') &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CC || this.cargoPaymentInfo?.operationMode == OperationMode.CL)
    );
  }

  onDoneClick() {
    if (this.paymentTransactionComponent) {
      this.paymentTransactionComponent.setPendoUrl('done');
    } else {
      console.error('PaymentTransactionComponent is not defined.');
    }

    if (
      this.transactionResponse === undefined ||
      (this.transactionResponse.transactionResult.isSuccess === false && this.currentAuthCode !== null)
    ) {
      const transactionResult: TransactionResult = {
        authorizationCode: this.currentAuthCode,
        isSuccess: true,
      };
      this.transactionResponse = {
        to: '',
        transactionResult: transactionResult,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        transaction: this.cargoPaymentInfo!,
      };
      if (this.isSendNoAuth() === true) {
        transactionResult.authorizationCode = this.NoAuth;
      }
    }
    if (!this.isDisabledNext) {
      if (this.isSendNoAuth() === true) {
        this.transactionResponse.transactionResult.authorizationCode = this.NoAuth;
      }
      this.sendTransactionResultToICargoOnDone(this.transactionResponse);
    } else if (this.buttonName == 'Done' && this.isDisabledNext && this.cargoPaymentInfo?.operationMode == OperationMode.CH) {
      this.sharedService.updateDoneClickDisabled(true);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnCash));
    }
  }

  private isNoAuth(): boolean {
    const sendNoAuth =
      (this.cargoPaymentInfo?.signatureRequired?.toLocaleUpperCase() === 'N' ||
        (this.cargoPaymentInfo?.signatureRequired?.toLocaleUpperCase() === 'Y' &&
          this.cargoPaymentInfo?.signatureRequirement === 'NotRequired')) &&
      this.cargoPaymentInfo?.paymentAdviceAmount === 0 &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CC || this.cargoPaymentInfo?.operationMode == OperationMode.CL) &&
      this.cargoPaymentInfo?.paymentAdviceNumber !== undefined &&
      this.cargoPaymentInfo?.paymentAdviceNumber !== 'null' &&
      this.cargoPaymentInfo?.paymentAdviceNumber !== null;

    return sendNoAuth;
  }

  private isSendNoAuth(): boolean {
    const sendNoAuth =
      this.cargoPaymentInfo?.paymentAdviceAmount != undefined &&
      this.cargoPaymentInfo?.paymentAdviceAmount <= 0 &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CC || this.cargoPaymentInfo?.operationMode == OperationMode.CL) &&
      this.cargoPaymentInfo?.paymentAdviceNumber !== undefined &&
      this.cargoPaymentInfo?.paymentAdviceNumber !== 'null' &&
      this.cargoPaymentInfo?.paymentAdviceNumber !== null;

    return sendNoAuth;
  }

  public sendTransactionResultToICargoOnDone(transactionResponse: TransactionResponse) {
    const startedAt = Date.now();
    this.logTelemetry(
      'CargoPaymentUI.PaymentFinalization.Started',
      this.createTelemetryProperties({
        transactionId: transactionResponse.transaction?.transactionId || this.transactionId || undefined,
        paymentAdviceNumber: transactionResponse.transaction?.paymentAdviceNumber,
        awb: transactionResponse.transaction?.awbNumber,
      })
    );
    this.paymentService.sendPaymentAuthorization(transactionResponse, true).subscribe({
      next: (response: PaymentAuthorizationResponse) => {
        console.log('PaymentAuthorization Response: ');
        if (response.isSuccess) {
          console.log('Successfully sent Transaction Result to iCargo');
          this.logTelemetry(
            'CargoPaymentUI.PaymentFinalization.Completed',
            this.createTelemetryProperties({
              transactionId: transactionResponse.transaction?.transactionId || this.transactionId || undefined,
              paymentAdviceNumber: transactionResponse.transaction?.paymentAdviceNumber,
              awb: transactionResponse.transaction?.awbNumber,
              outcome: 'success',
              durationMs: Date.now() - startedAt,
            })
          );
          const screenMode = 'confirmScreenAction';
          const paymentAdvice = this.transactionResponse?.transaction?.paymentAdviceNumber || '';
          const awb = this.transactionResponse?.transaction?.awbNumber || '';
          //This validation is mandatory because redirection breaks jasmin test, in test set this value to true
          if (this.isJasmineTest === false) {
            const hrefUrl =
              environment.iCargoBaseUrlRedirect + '?screenMode=' + screenMode + '&paymentAdvice=' + paymentAdvice + '&awb=' + awb;
            console.log('href iCargo: ' + hrefUrl);
            window.location.href = hrefUrl;
          }
        } else {
          this.logTelemetry(
            'CargoPaymentUI.PaymentFinalization.Failed',
            this.createTelemetryProperties({
              transactionId: transactionResponse.transaction?.transactionId || this.transactionId || undefined,
              paymentAdviceNumber: transactionResponse.transaction?.paymentAdviceNumber,
              awb: transactionResponse.transaction?.awbNumber,
              failureCode: response.errorCode ?? response.responseCode ?? 'unsuccessful-response',
              outcome: 'failed',
              durationMs: Date.now() - startedAt,
            })
          );
          this.showErrorRedirect(response.errorCode ?? response.responseCode ?? 'unsuccessful-response');
        }
      },
      error: (err: unknown) => {
        console.log('Error sending Transaction Result to iCargo');
        this.logTelemetry(
          'CargoPaymentUI.PaymentFinalization.Failed',
          this.createTelemetryProperties({
            transactionId: transactionResponse.transaction?.transactionId || this.transactionId || undefined,
            paymentAdviceNumber: transactionResponse.transaction?.paymentAdviceNumber,
            awb: transactionResponse.transaction?.awbNumber,
            httpStatus: this.getHttpStatus(err),
            failureCode: this.getFailureCode(err),
            outcome: 'failed',
            durationMs: Date.now() - startedAt,
          })
        );
        this.showErrorRedirect(this.getFailureCode(err));
      },
    });
  }

  showErrorRedirect(failureCode?: string) {
    this.logTelemetry(
      'CargoPaymentUI.OutcomeUnknown.Entered',
      this.createTelemetryProperties({
        failureCode,
      })
    );
    this.sharedService.updateIsICargoResultWithError(true);
    if (this.currentAuthCode !== null) {
      mapMessage.set(CustomMessage.ErrorRedirect, {
        text:
          'Error: Manual authorization required. Before closing your browser, please record this authorization code for manager override: <b>' +
          this.currentAuthCode +
          '.</b>',
        type: CustomMessageType.Error,
      });
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorRedirect));
    } else {
      mapMessage.set(CustomMessage.ErrorRedirect, {
        text: 'Error: Redirect failed. Please close the browser window and re-initiate transaction.',
        type: CustomMessageType.Error,
      });
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorRedirect));
    }
  }

  onEmitModalType(modalType: string) {
    this.modalTypeEvent.emit(modalType);
  }

  public onClickCreditCard(): void {
    if (!this.currentManualCardEntryButtonDisabled) {
      this.sharedService.updateManualCardEntryClick(true);
      this.onEmitModalType('creditCard');
    }
  }

  public onExitToiCargo(): void {
    if (!this.isDisabledExit) {
      this.onEmitModalType('exitToiCargo');
    }
  }

  public isButtonDisabled(): boolean {
    return this.cargoPaymentInfo?.signatureRequired?.toLocaleLowerCase() === 'y' &&
      this.cargoPaymentInfo?.signatureRequirement === undefined
      ? true
      : false;
  }

  public async getTransactionResultFromIPad(): Promise<void> {
    if (this.signalRService.connection.state === undefined || this.signalRService.connection.state === HubConnectionState.Disconnected) {
      await this.signalRService.connectToSignalR();
      console.log('Subscribing to Event AcknowledgeMessage');
      this.signalRService.connection.on('AcknowledgeMessage', (response: AcknowledgeResponse) => {
        const acknowledgementEventId = response?.transactionId;
        const waitStartTransactionId = this.acknowledgementWaitTransactionId;
        const hasActiveWait = this.acknowledgementWaitStartedAt !== undefined;
        const classification: 'Matching' | 'Stale' | 'Unknown' =
          acknowledgementEventId === undefined || acknowledgementEventId.length === 0
            ? 'Unknown'
            : !hasActiveWait || waitStartTransactionId === undefined || waitStartTransactionId.length === 0
            ? 'Unknown'
            : acknowledgementEventId === waitStartTransactionId
            ? 'Matching'
            : 'Stale';
        const durationMs = this.acknowledgementWaitStartedAt ? Date.now() - this.acknowledgementWaitStartedAt : undefined;

        this.acknowledgementEventId = acknowledgementEventId;
        this.acknowledgementEventClassification = classification;

        if (classification === 'Matching' && acknowledgementEventId !== undefined) {
          this.transactionId = acknowledgementEventId;
        }

        const acknowledgementProperties = this.createTelemetryProperties({
          transactionId: acknowledgementEventId,
          acknowledgementEventId,
          acknowledgementClassification: classification,
          waitStartTransactionId,
          waitStartState: this.acknowledgementWaitState,
          durationMs,
          outcome: classification === 'Matching' ? 'succeeded' : 'unknown',
          failureCode:
            classification === 'Matching' ? undefined : classification === 'Stale' ? 'stale-acknowledgement' : 'acknowledgement-id-unknown',
        });

        this.logTelemetry(
          classification === 'Matching' ? 'CargoPaymentUI.AcknowledgementWait.Received' : 'CargoPaymentUI.AcknowledgementWait.Ignored',
          acknowledgementProperties
        );

        if (
          classification === 'Matching' &&
          acknowledgementEventId !== undefined &&
          this.cargoPaymentInfo?.operationMode === OperationMode.CH &&
          (this.cargoPaymentInfo?.signatureRequirement === 'NotRequired' || this.cargoPaymentInfo?.signatureRequirement === undefined)
        ) {
          const transactionResult: TransactionResult = {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            isSuccess: true,
            authorizationCode: null,
          };
          this.transactionResponse = {
            transactionResult: transactionResult,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            transaction: this.cargoPaymentInfo,
          };
          this.showCashPaymentSectionResult();
        }
      });

      console.log('Subscribing to Event NewTransactionResult');
      this.signalRService.connection.on('NewTransactionResult', (message: TransactionResponse) => {
        this.sendAcknowledgeMessage();
        this.transactionResponse = message;
        this.logTelemetry(
          'CargoPaymentUI.TransactionResult.Received',
          this.createTelemetryProperties({
            transactionId: message.transaction?.transactionId || this.transactionId || undefined,
            paymentAdviceNumber: message.transaction?.paymentAdviceNumber,
            awb: message.transaction?.awbNumber,
          })
        );
        this.showSignatureSectionResult(); // Only signature scenario.
        this.showPaymentSectionResult(); // SUCCESS: Only payment scenario, no signature.
        this.showErrorPaymentSectionResult(); // ERROR: Only payment scenario, no signature.
        this.showCashPaymentSectionResult(); // Only cash scenario.
        this.showPaymentSignatureSectionResult(); // SUCCESS: Payment + signature scenario.
        this.showErrorSignaturePaymentSectionResult(); // ERROR: Payment + signature scenario.
        this.showCancelResult(); // Canceled by user scenario.
        this.showMobyDeviceError(); // Moby device error scenario. This is not handling due to an iOS app issue.
        this.showCancelResult(); // Moby device error scenario. This is not handling due to an iOS app issue.
        this.showTimeoutError();
        if (!this.isTransactionResultHandled()) {
          this.logTelemetry(
            'CargoPaymentUI.TransactionResult.Ignored',
            this.createTelemetryProperties({
              transactionId: message.transaction?.transactionId || this.transactionId || undefined,
              paymentAdviceNumber: message.transaction?.paymentAdviceNumber,
              awb: message.transaction?.awbNumber,
              outcome: 'ignored',
              failureCode: 'no-matching-handler',
            })
          );
        }
      });
    }
  }

  showTimeoutError() {
    if (this.validateTimeoutError()) {
      this.sharedService.updateIsCurrentTimeoutError(true);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorCustomTimeOutFromIPad));
      this.canceledResponse();
    }
  }

  validateTimeoutError(): boolean {
    return (
      this.transactionResponse != undefined &&
      this.transactionResponse.transactionResult.isSuccess === false &&
      this.transactionResponse.transactionResult.errorMessage === InactivityErrorInIPad.CustomTimeoutError
    );
  }

  onContinue() {
    if (this.modalType === 'confirmation') {
      if (this.isWarningToExit() || this.isValidatedAmountAndCashOrCheck()) {
        this.sharedService.updateValidTransaction(false);
        this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSignaturePayment));
        this.modalService.close();
        this.sharedService.updateNextDisabled(true);
      } else {
        this.showContractSection(false);
        this.sendRequestToIpad(true);
      }
    }
  }

  onManualContinue() {
    if (this.modalType === 'manualConfirmation') {
      if (this.isWarningToExit()) {
        this.sharedService.updateNextDisabled(true);
        this.sharedService.updateContinueDeviceClick(false);
        this.sharedService.updateValidTransaction(false);
        this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSignaturePayment));
        this.modalService.close();
      } else {
        if (this.cargoPaymentInfo?.operationMode == OperationMode.CC) {
          this.showPaymentSection();
          this.sharedService.updateContinueDeviceClick(false);
          this.modalService.close();
          this.sharedService.updateManualCardEntryClick(true);
          this.onEmitModalType('creditCard');
        } else if (this.cargoPaymentInfo?.operationMode == OperationMode.CL) {
          this.showPaymentSection();
          this.sharedService.updateContinueDeviceClick(false);
          this.modalService.close();
          this.sharedService.updateManualCardEntryClick(true);
          this.showLoadingPaymentSection();
          this.sendRequestToAtlasCommercialAccount();
        } else if (this.cargoPaymentInfo?.operationMode == OperationMode.CH) {
          this.showPaymentSection();
          this.sharedService.updateNextClick(true);
          this.sharedService.updateContinueClick(true);
          this.sharedService.updateContinueDeviceClick(false);
          this.modalService.close();
          this.sharedService.updateManualCardEntryClick(true);
          this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnCash));
          this.sharedService.updateCashPayment(true);
          this.successfulCashResponse();
        }
      }
    }
  }

  public sendRequestToAtlasCommercialAccount() {
    const amount = Number(this.cargoPaymentInfo?.paymentAdviceAmount ?? 0);
    const currency = String(this.cargoPaymentInfo?.paymentAdviceCurrency ?? 'USD');
    const accountNumber = String(this.cargoPaymentInfo?.accountNumber ?? '');
    const awb = this.cargoPaymentInfo?.awbNumber?.split('-');
    const airlineCode = awb?.[0].trim();
    const documentNumber = awb?.[1].trim();
    const airportCode = this.cargoPaymentInfo?.airportCode;
    const passengerName = this.cargoPaymentInfo?.customerName;

    const payload = new AtlasRequestCommercialAccount(
      accountNumber,
      amount,
      currency,
      this.requestTransactionId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      airlineCode!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      documentNumber!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      airportCode!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      passengerName!
    );

    this.paymentService.sendCommercialAccount(payload).subscribe({
      next: (response: AtlasResponse) => {
        console.log('Successfully sent Transaction Result to atlas commercial account');
        const transactionResult: TransactionResult = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          authorizationCode: response.authorizationCode!,
          isSuccess: response.isSuccess,
          errorCode: response.errorCode,
        };
        this.transactionResponse = {
          transactionResult: transactionResult,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          transaction: this.cargoPaymentInfo!,
        };
        this.sharedService.updateSubmitBluefinClick(false);
        this.showPaymentSectionResult();
        this.sharedService.updateIsHiddenCreditCard(false);
      },
      error: (errorResponse) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const response = errorResponse.error as AtlasResponse;
        const transactionResult: TransactionResult = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          errorMessage: response.errorCode,
          isSuccess: false,
          authorizationCode: null,
        };
        this.transactionResponse = {
          transactionResult: transactionResult,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          transaction: this.cargoPaymentInfo!,
        };

        if (response.errorCode != undefined && this.sharedService.existErrorCode(response.errorCode)) {
          this.showErrorPaymentSectionResult();
        } else {
          this.showErrorSection();
        }
      },
    });
  }

  showErrorSection(): void {
    if (this.paymentType == 'Manual Payment') {
      this.sharedService.updateContinueClick(false);
      this.sharedService.updateNextDisabled(true);
    }
    this.sharedService.updateExitICargoDisabled(false);
    this.sharedService.updateLoadingState(false);
    this.sharedService.updateBluefinError(true);
    this.sharedService.updateSubmitBluefinClick(false);
    this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSomething));
  }

  showLoadingPaymentSection(): void {
    if (this.paymentType == 'Manual Payment') {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoPaymentProgress));
      this.sharedService.updateContinueClick(true);
    }
    this.sharedService.updateExitICargoDisabled(true);
    this.sharedService.updateLoadingState(true);
    this.sharedService.updateManualCardEntryButtonDisabled(true);
    this.sharedService.updateSubmitBluefinClick(true);
  }

  isWarningToExit(): boolean {
    return this.cargoPaymentInfo?.signatureRequirement == 'NotRequired' && this.cargoPaymentInfo?.paymentAdviceAmount <= 0 ? true : false;
  }

  isValidatedAmountAndCashOrCheck(): boolean {
    return this.cargoPaymentInfo?.signatureRequirement == 'NotRequired' &&
      this.paymentType === 'Manual Payment' &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CH ||
        this.cargoPaymentInfo?.operationMode == OperationMode.CQ ||
        this.cargoPaymentInfo?.operationMode == undefined)
      ? true
      : false;
  }

  sendRequestToIpad(isSignatureRequired: boolean) {
    if (this.isWarningToExit() || this.isValidatedAmountAndCashOrCheck()) {
      this.sharedService.updateValidTransaction(false);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSignaturePayment));
      this.modalService.close();
    } else {
      if (isSignatureRequired) {
        this.validateLoadingState(true);
        if (this.cargoPaymentInfo) {
          this.signalRService.sendRequestToIpad(this.cargoPaymentInfo).subscribe({
            next: () => {
              console.log('RequestToIpad Sent');
              this.modalService.close();
              this.checkAcknowledgeResponse();
            },
            error: () => {
              console.log('Error Sending RequestToIpad');
              this.modalService.close();
              this.showAcknowledgeMessageError(this.cargoPaymentInfo?.signatureRequired, 'transaction-send-failed');
            },
          });
        }
      } else {
        this.validateLoadingState(false);
        if (this.cargoPaymentInfo) {
          this.signalRService.sendRequestToIpad(this.cargoPaymentInfo).subscribe({
            next: () => {
              console.log('RequestToIpad Sent');
              this.checkAcknowledgeResponse();
            },
            error: () => {
              console.log('Error Sending RequestToIpad');
              this.showAcknowledgeMessageError(this.cargoPaymentInfo?.signatureRequired, 'transaction-send-failed');
            },
          });
        }
      }
    }
  }

  checkAcknowledgeResponse() {
    let count = environment.acknowledgeTimeout;
    if (this.acknowledgementWaitTimer !== undefined) {
      clearInterval(this.acknowledgementWaitTimer);
    }

    this.acknowledgementWaitStartedAt = Date.now();
    this.acknowledgementWaitTransactionId =
      this.transactionId || this.requestTransactionId || this.cargoPaymentInfo?.transactionId || undefined;
    this.acknowledgementWaitState = this.getHubState(this.signalRService.connection?.state);
    this.acknowledgementEventId = undefined;
    this.acknowledgementEventClassification = undefined;
    this.logTelemetry(
      'CargoPaymentUI.AcknowledgementWait.Started',
      this.createTelemetryProperties({
        transactionId: this.acknowledgementWaitTransactionId,
        waitStartTransactionId: this.acknowledgementWaitTransactionId,
        waitStartState: this.acknowledgementWaitState,
        observedAcknowledgementId: undefined,
        outcome: 'started',
      })
    );
    this.acknowledgementWaitTimer = setInterval(() => {
      // Preserve the existing timeout/UX behavior: the timer stops when the shared
      // transaction id becomes populated. Telemetry separately records whether the
      // value came from a matching acknowledgement event or was stale/unknown.
      if (this.transactionId.length > 0) {
        clearInterval(this.acknowledgementWaitTimer);
        this.acknowledgementWaitTimer = undefined;
        this.clearAcknowledgementWait();
        return;
      }

      count -= 1;
      if (count <= 0) {
        clearInterval(this.acknowledgementWaitTimer);
        this.acknowledgementWaitTimer = undefined;
        const observedAcknowledgementId = this.acknowledgementEventId;
        const observedClassification = this.acknowledgementEventClassification ?? 'None';
        this.logTelemetry(
          'CargoPaymentUI.AcknowledgementWait.Timeout',
          this.createTelemetryProperties({
            transactionId: this.acknowledgementWaitTransactionId,
            waitStartTransactionId: this.acknowledgementWaitTransactionId,
            waitStartState: this.acknowledgementWaitState,
            observedAcknowledgementId,
            observedAcknowledgementClassification: observedClassification,
            durationMs: this.acknowledgementWaitStartedAt ? Date.now() - this.acknowledgementWaitStartedAt : undefined,
            failureCode: observedClassification === 'Stale' ? 'acknowledgement-timeout-after-stale' : 'acknowledgement-timeout',
            outcome: 'timedOut',
          })
        );
        this.clearAcknowledgementWait();
        this.showAcknowledgeMessageError(this.cargoPaymentInfo?.signatureRequired, 'acknowledgement-timeout');
      }
    }, 1000);
  }

  private clearAcknowledgementWait(): void {
    this.acknowledgementWaitStartedAt = undefined;
    this.acknowledgementWaitTransactionId = undefined;
    this.acknowledgementWaitState = undefined;
  }

  showAcknowledgeMessageError(signatureRequired: string | undefined, failureCode = 'connection-warning') {
    this.logTelemetry(
      'CargoPaymentUI.ConnectionWarning.Entered',
      this.createTelemetryProperties({
        failureCode,
      })
    );
    this.showContractSection(true);
    if (signatureRequired != undefined && signatureRequired.toLowerCase() === 'y') {
      this.sharedService.updateNextDisabled(true);
    } else {
      this.sharedService.updateNextDisabled(false);
    }

    this.sharedService.updateLoadingState(false);
    this.sharedService.updateNextClick(false);
    this.sharedService.updateContinueClick(false);
    this.sharedService.updateContinueDeviceClick(false);
    this.sharedService.updateConnectedIpad(true);
    this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnConnect));
    this.sharedService.updateSelectedIpad(null);
    this.sharedService.updateExitICargoDisabled(false);
  }

  validateLoadingState(isSignatureRequired: boolean) {
    if (isSignatureRequired) {
      if (
        (this.cargoPaymentInfo?.signatureRequirement == 'NotRequired' && this.cargoPaymentInfo?.paymentAdviceAmount <= 0) ||
        (this.cargoPaymentInfo?.signatureRequirement == 'NotRequired' &&
          (this.cargoPaymentInfo?.operationMode == OperationMode.CQ || this.cargoPaymentInfo?.operationMode == undefined))
      ) {
        this.showContractSection(true);
        this.sharedService.updateContinueClick(true);
        this.modalService.close();
        this.sharedService.updateNextDisabled(true);
      } else {
        this.validateOperationMode();
      }
    } else {
      this.sharedService.updateNextClick(true);
      this.sharedService.updateLoadingState(true);
      this.sharedService.updateExitICargoDisabled(true);
      this.showPaymentSection();
    }
  }

  validateOperationMode() {
    this.modalService.close();
    this.sharedService.updateContinueClick(true);
    this.sharedService.updateNextClick(true);
    this.sharedService.updateLoadingState(true);
    this.sharedService.updateExitICargoDisabled(true);
    if (
      this.cargoPaymentInfo?.signatureRequirement != 'NotRequired' &&
      (this.cargoPaymentInfo?.paymentAdviceAmount == undefined || this.cargoPaymentInfo?.paymentAdviceAmount <= 0)
    ) {
      this.showPaymentSection(true);
      console.log('op');
      console.log(this.cargoPaymentInfo?.operationMode);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoSignatureProgress));
    } else if (
      this.cargoPaymentInfo?.signatureRequirement == 'NotRequired' &&
      this.cargoPaymentInfo?.paymentAdviceAmount > 0 &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CC ||
        this.cargoPaymentInfo?.operationMode == OperationMode.CL ||
        this.cargoPaymentInfo?.operationMode == OperationMode.CH)
    ) {
      this.showPaymentSection();
      this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoPaymentProgress));
    } else if (
      this.cargoPaymentInfo?.signatureRequirement != 'NotRequired' &&
      this.cargoPaymentInfo?.paymentAdviceAmount > 0 &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CC || this.cargoPaymentInfo?.operationMode == OperationMode.CL)
    ) {
      this.showPaymentSection();
      this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoSignaturePaymentProgress));
    } else {
      this.showPaymentSection(true);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.InfoSignatureProgress));
    }
  }

  sendAcknowledgeMessage() {
    this.signalRService.sendAcknowledgeMessage().subscribe({
      next: () => {
        console.log('AcknowledgeResponse Sent');
      },
      error: () => {
        console.log('Error Sending AcknowledgeResponse');
        this.modalService.close();
      },
    });
  }

  showMobyDeviceError() {
    if (this.validateMobyError()) {
      this.sharedService.updateMobyDeviceError(true);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorMoby));
      this.sharedService.updateLoadingState(false);
      this.sharedService.updateExitICargoDisabled(false);
      this.sharedService.updateNextDisabled(true);
      this.sharedService.updateErrorPaymentMessage('Error: Card reader is inactive. Please contact IT support.');
    }
  }

  validateMobyError(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse?.transaction.operationMode == OperationMode.CC &&
      this.transactionResponse?.transaction.paymentAdviceAmount > 0 &&
      this.transactionResponse.transactionResult.isSuccess === false &&
      (this.transactionResponse.transactionResult.authorizationCode == null ||
        this.transactionResponse.transactionResult.authorizationCode.length === 0) &&
      this.transactionResponse.transactionResult.errorMessage === MobyError.DeviceInactive
      ? true
      : false;
  }

  showContractSection(isError: boolean) {
    if (isError) {
      this.sharedService.updateContractSectionExpanded(false);
      this.showPaymentSection();
    } else {
      this.sharedService.updateContractSectionExpanded(true);
    }
  }

  showPaymentSection(isHidden = false) {
    if (this.cargoPaymentInfo?.signatureRequirement == undefined || this.cargoPaymentInfo?.signatureRequirement == 'NotRequired') {
      this.sharedService.updatePaymentSectionExpanded(true);
    } else {
      // For signature required cases, expand unless it's Cash operation mode
      if (this.cargoPaymentInfo?.operationMode != OperationMode.CH) {
        this.sharedService.updatePaymentSectionExpanded(true);
      }
    }
    this.sharedService.updatePaymentSectionHidden(isHidden);
  }

  private createTelemetryProperties(properties: { [key: string]: unknown } = {}): { [key: string]: unknown } {
    const telemetryProperties = {
      correlationId: this.requestTransactionId || undefined,
      paymentAdviceNumber: this.cargoPaymentInfo?.paymentAdviceNumber,
      awb: this.cargoPaymentInfo?.awbNumber,
      transactionId: this.transactionId || undefined,
      browserDeviceId: this.signalRService.connectedDevice?.deviceId,
      ipadDeviceId: this.currentSelectedIpad?.deviceId ?? this.signalRService.connectedDevice?.pairedDeviceId,
      browserConnectionId: this.signalRService.connection?.connectionId ?? undefined,
      ipadConnectionId: undefined,
      connectionId: this.signalRService.connection?.connectionId ?? undefined,
      hubState: this.getHubState(this.signalRService.connection?.state),
      ...properties,
    };

    return Object.fromEntries(Object.entries(telemetryProperties).filter(([, value]) => value !== undefined && value !== null));
  }

  private logTelemetry(eventName: string, properties: { [key: string]: unknown }): void {
    this.monitoringService.logEvent(eventName, properties);
  }

  private getHubState(state: HubConnectionState | string | undefined): string | undefined {
    if (state === undefined) {
      return undefined;
    }

    return typeof state === 'string' ? state : HubConnectionState[state] ?? String(state);
  }

  private getHttpStatus(error: unknown): number | undefined {
    if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
      return error.status;
    }

    return undefined;
  }

  private getFailureCode(error: unknown): string | undefined {
    if (typeof error === 'object' && error !== null) {
      if ('error' in error && typeof error.error === 'object' && error.error !== null && 'errorCode' in error.error) {
        return typeof error.error.errorCode === 'string' ? error.error.errorCode : undefined;
      }

      if ('name' in error && typeof error.name === 'string') {
        return error.name;
      }
    }

    return error instanceof Error ? error.name : undefined;
  }

  validateSignatureObject1(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse.transactionResult.signatureObject1 != undefined &&
      this.transactionResponse.transactionResult.signatureObject1.length > 0 &&
      (this.transactionResponse.transaction.signatureRequirement == 'AWB' ||
        this.transactionResponse.transaction.signatureRequirement == 'Security' ||
        this.transactionResponse.transaction.signatureRequirement == 'Delivery')
      ? true
      : false;
  }

  validateSignatureObject2(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse.transactionResult.signatureObject1 != undefined &&
      this.transactionResponse.transactionResult.signatureObject1.length > 0 &&
      this.transactionResponse.transactionResult.signatureObject2 != undefined &&
      this.transactionResponse.transactionResult.signatureObject2.length > 0 &&
      this.transactionResponse.transaction.signatureRequirement == 'AWBSecurity'
      ? true
      : false;
  }

  showSignatureSectionResult() {
    if (this.validateSignatureObject1()) {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessSignature));
      this.sharedService.updateSignatureObject1(true);
      this.successfulResponse();
    } else if (this.validateSignatureObject2()) {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessSignature));
      this.sharedService.updateSignatureObject1(true);
      this.sharedService.updateSignatureObject2(true);
      this.successfulResponse();
    }
  }

  showPaymentSectionResult() {
    if (
      this.transactionResponse !== undefined &&
      this.validatePaymentResult() &&
      !this.validateSignatureObject1() &&
      !this.validateSignatureObject2()
    ) {
      if (this.transactionResponse?.transaction.operationMode == OperationMode.CC) {
        this.sharedService.updatePaymentMessage('Credit Card');
      } else if (this.transactionResponse?.transaction.operationMode == OperationMode.CL) {
        this.sharedService.updatePaymentMessage('AS Account');
      }

      this.sharedService.updateSuccessfulPayment(true);

      const authCode = this.transactionResponse.transactionResult.authorizationCode?.toString() ?? '';
      mapMessage.set(CustomMessage.SuccessPayment, {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        text: 'Payment successfully completed. <b>Auth code  - ' + authCode + ' </b>',
        type: CustomMessageType.Success,
      });
      const mapMessageToShow =
        this.transactionResponse.transactionResult.errorCode === DuplicateTransactionError.DuplicateOrder ||
        this.transactionResponse.transactionResult.errorMessage === DuplicateTransactionError.DuplicateOrder
          ? CustomMessage.InfoPaymentCollected
          : CustomMessage.SuccessPayment;

      this.sharedService.showMessage(mapMessage.get(mapMessageToShow));
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      this.sharedService.updateAuthCode(authCode);
      this.successfulResponse();
    }
  }

  showPaymentSignatureSectionResult() {
    if (
      this.transactionResponse?.transactionResult !== undefined &&
      this.validateSignaturePaymentResult() &&
      (this.validateSignatureObject1() || this.validateSignatureObject2())
    ) {
      if (this.transactionResponse?.transaction.operationMode == OperationMode.CC) {
        this.sharedService.updatePaymentMessage('Credit Card');
      } else if (this.transactionResponse?.transaction.operationMode == OperationMode.CL) {
        this.sharedService.updatePaymentMessage('AS Account');
      }
      this.showSignatureSectionResult();
      this.sharedService.updateSuccessfulPayment(true);

      const authCode = this.transactionResponse.transactionResult.authorizationCode?.toString() ?? '';
      mapMessage.set(CustomMessage.SuccessPayment, {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        text: 'Signatures and payment successfully completed. <b>Auth code  - ' + authCode + ' </b>',
        type: CustomMessageType.Success,
      });
      const mapMessageToShow =
        this.transactionResponse.transactionResult.errorMessage === DuplicateTransactionError.DuplicateOrder
          ? CustomMessage.InfoPaymentCollected
          : CustomMessage.SuccessPayment;

      this.sharedService.showMessage(mapMessage.get(mapMessageToShow));
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      this.sharedService.updateAuthCode(authCode);
    }
  }

  showCashPaymentSectionResult() {
    if (this.validateCashResult() && this.validateSignatureObject1()) {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessSignature));
      this.sharedService.updateSignatureObject1(true);
      this.sharedService.updateCashPayment(true);
      this.successfulCashResponse();
      this.showPaymentSection();
    } else if (this.validateCashResult() && this.validateSignatureObject2()) {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessSignature));
      this.sharedService.updateSignatureObject1(true);
      this.sharedService.updateSignatureObject2(true);
      this.sharedService.updateCashPayment(true);
      this.successfulCashResponse();
      this.showPaymentSection();
    } else if (this.validateCashResult() && !this.validateSignatureObject1()) {
      this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnCash));
      this.sharedService.updateCashPayment(true);
      this.successfulCashResponse();
    }
  }

  showErrorPaymentSectionResult() {
    if (this.validatePaymentErrorResult() && !this.validateSignatureObject1() && !this.validateSignatureObject2()) {
      if (
        this.sharedService.existErrorCode(
          this.transactionResponse?.transactionResult.errorMessage != undefined
            ? this.transactionResponse?.transactionResult.errorMessage
            : ''
        )
      ) {
        if (
          this.transactionResponse?.transactionResult.errorCode === AtlasErrorCodes.CreditAccountError ||
          this.transactionResponse?.transactionResult.errorMessage === AtlasErrorCodes.CreditAccountError
        ) {
          this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorCreditAccount));
          this.sharedService.updateErrorPaymentMessage('Issue with billing account - Limit exceeded, blocked etc.');
        } else {
          this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorDeclined));
          this.sharedService.updateErrorPaymentMessage('Transaction declined. Please exit to iCargo to try another form of payment.');
        }

        this.sharedService.updateSubmitBluefinClick(false);
        this.sharedService.updateErrorCreditCard(true);
        this.canceledResponse();
      } else {
        this.resetForm();
      }
    }
  }

  showErrorSignaturePaymentSectionResult() {
    if (this.validateSignaturePaymentErrorResult() && (this.validateSignatureObject1() || this.validateSignatureObject2())) {
      if (
        this.sharedService.existErrorCode(
          this.transactionResponse?.transactionResult.errorMessage != undefined
            ? this.transactionResponse?.transactionResult.errorMessage
            : ''
        )
      ) {
        if (
          this.transactionResponse?.transactionResult.errorCode === AtlasErrorCodes.CreditAccountError ||
          this.transactionResponse?.transactionResult.errorMessage === AtlasErrorCodes.CreditAccountError
        ) {
          this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorCreditAccount));
          this.sharedService.updateErrorPaymentMessage('Issue with billing account - Limit exceeded, blocked etc.');
        } else {
          this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorDeclined));
          this.sharedService.updateErrorPaymentMessage('Transaction declined. Please exit to iCargo to try another form of payment.');
        }
        this.sharedService.updateErrorCreditCard(true);
        this.canceledResponse();
      } else {
        this.resetForm();
      }
    }
  }

  resetForm() {
    this.sharedService.updateLoadingState(false);
    this.sharedService.updateErrorCreditCard(false);
    this.sharedService.updateBluefinError(false);
    this.sharedService.updateNextClick(false);
    this.sharedService.updateContinueClick(false);
    this.sharedService.updateSignatureObject1(false);
    this.showContractSection(true);
    this.sharedService.updateNextButtonName('Next');
    this.sharedService.updateSomethingHappened(true);
    this.sharedService.updateNextDisabled(true);
    this.sharedService.updateExitICargoDisabled(false);
    this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSomething));
  }

  showCancelResult() {
    if (
      this.transactionResponse !== undefined &&
      this.validateCancelResult() &&
      !this.validateSignatureObject1() &&
      !this.validateSignatureObject2()
    ) {
      if (this.transactionResponse?.transaction.operationMode == OperationMode.CC) {
        this.sharedService.updatePaymentMessage('Credit Card');
      } else if (this.transactionResponse?.transaction.operationMode == OperationMode.CL) {
        this.sharedService.updatePaymentMessage('AS Account');
      }
      this.sharedService.updateErrorCreditCard(false);
      this.sharedService.updateCanceledByUser(true);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.ErrorCanceled));
      this.canceledResponse();
    }
  }

  validateCancelResult(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse.transactionResult.isSuccess === false &&
      (this.transactionResponse.transactionResult.authorizationCode == null ||
        this.transactionResponse.transactionResult.authorizationCode.length === 0) &&
      this.transactionResponse.transactionResult.errorMessage === 'Canceled by user!'
      ? true
      : false;
  }

  validatePaymentResult(): boolean {
    return this.transactionResponse != undefined &&
      (this.transactionResponse.transaction.operationMode === OperationMode.CC ||
        this.transactionResponse.transaction.operationMode === OperationMode.CL) &&
      this.transactionResponse?.transaction.paymentAdviceAmount > 0 &&
      this.transactionResponse.transactionResult.isSuccess === true &&
      this.transactionResponse.transactionResult.authorizationCode != null &&
      this.transactionResponse.transactionResult.authorizationCode.length > 0 &&
      (this.transactionResponse.transaction.signatureRequirement == 'NotRequired' ||
        this.transactionResponse.transaction.signatureRequirement == undefined)
      ? true
      : false;
  }

  validateSignaturePaymentResult(): boolean {
    return this.transactionResponse != undefined &&
      (this.transactionResponse.transaction.operationMode === OperationMode.CC ||
        this.transactionResponse.transaction.operationMode === OperationMode.CL) &&
      this.transactionResponse.transaction.paymentAdviceAmount > 0 &&
      this.transactionResponse.transactionResult.isSuccess === true &&
      this.transactionResponse.transactionResult.authorizationCode != null &&
      this.transactionResponse.transactionResult.authorizationCode.length >= 0 &&
      (this.transactionResponse.transaction.signatureRequirement !== 'NotRequired' ||
        this.transactionResponse.transaction.signatureRequirement !== undefined)
      ? true
      : false;
  }

  validatePaymentErrorResult(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse?.transaction.paymentAdviceAmount > 0 &&
      (this.transactionResponse?.transaction.operationMode == OperationMode.CC ||
        this.transactionResponse?.transaction.operationMode == OperationMode.CL) &&
      this.transactionResponse.transactionResult.isSuccess === false &&
      (this.transactionResponse.transactionResult.authorizationCode == null ||
        this.transactionResponse.transactionResult.authorizationCode.length === 0) &&
      (this.transactionResponse.transaction.signatureRequirement == 'NotRequired' ||
        this.transactionResponse.transaction.signatureRequirement == undefined) &&
      this.transactionResponse.transactionResult.errorMessage !== MobyError.DeviceInactive
      ? true
      : false;
  }

  validateSignaturePaymentErrorResult(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse?.transaction.paymentAdviceAmount > 0 &&
      (this.transactionResponse?.transaction.operationMode == OperationMode.CC ||
        this.transactionResponse?.transaction.operationMode == OperationMode.CL) &&
      this.transactionResponse.transactionResult.isSuccess === false &&
      (this.transactionResponse.transactionResult.authorizationCode == null ||
        this.transactionResponse.transactionResult.authorizationCode.length === 0) &&
      this.transactionResponse.transaction.signatureRequirement != undefined &&
      this.transactionResponse.transaction.signatureRequirement != 'NotRequired' &&
      this.transactionResponse.transactionResult.errorMessage !== MobyError.DeviceInactive
      ? true
      : false;
  }

  validateCashResult(): boolean {
    return this.transactionResponse != undefined &&
      this.transactionResponse?.transaction.operationMode == OperationMode.CH &&
      this.transactionResponse?.transaction.paymentAdviceAmount > 0
      ? true
      : false;
  }

  canceledResponse() {
    this.sharedService.updateNextButtonName('Done');
    this.sharedService.updateLoadingState(false);
    this.sharedService.updateExitICargoDisabled(false);
    this.sharedService.updateNextDisabled(true);
  }

  successfulResponse() {
    this.sharedService.updateNextButtonName('Done');
    this.sharedService.updateLoadingState(false);
    this.sharedService.updateExitICargoDisabled(true);
  }

  successfulCashResponse() {
    this.sharedService.updateNextButtonName('Done');
    this.sharedService.updateIsNoSignatureCash(true);
    this.sharedService.updateLoadingState(false);
    this.sharedService.updateNextDisabled(true);
    this.sharedService.updateExitICargoDisabled(false);
  }

  private isTransactionResultHandled(): boolean {
    const hasSignatureObject1 = this.validateSignatureObject1();
    const hasSignatureObject2 = this.validateSignatureObject2();
    const hasSignature = hasSignatureObject1 || hasSignatureObject2;

    return (
      hasSignature ||
      (this.validatePaymentResult() && !hasSignature) ||
      (this.validatePaymentErrorResult() && !hasSignature) ||
      this.validateCashResult() ||
      (this.validateSignaturePaymentResult() && hasSignature) ||
      (this.validateSignaturePaymentErrorResult() && hasSignature) ||
      (this.validateCancelResult() && !hasSignature) ||
      this.validateMobyError() ||
      this.validateTimeoutError()
    );
  }

  initializeInputParameterValidation(): void {
    if (this.inputParametersValidationResult === null || this.inputParametersValidationResult === undefined) {
      this.inputParametersValidationResult = [true, new Set<string>()];
    }
    this.isInputParametersValid = this.inputParametersValidationResult?.[0];
  }
}
